import { ValidatorRules, ValidatorRule, FieldContext, FieldData } from './types';
import { ValidationRuleResult } from './ValidationRuleResult';
import type Language from '../../language';

export class ValidationResult {
    private readonly validationResults: ValidationRuleResult[];

    constructor(results: ValidationRuleResult[]) {
        this.validationResults = results;
    }

    /** Checks if all validation rules have passed */
    get isValid(): boolean {
        return this.validationResults.every(result => !!result.isValid);
    }

    /** Checks if any validation rule returned an error */
    hasError(isValidatingForm = false): boolean {
        return Boolean(this.getError(isValidatingForm));
    }

    /** Returns the first validation result that returned an error */
    getError(isValidatingForm = false): ValidationRuleResult | null {
        return this.validationResults.find(result => result.hasError(isValidatingForm)) ?? null;
    }

    /** Returns all validation results that returned an error */
    getAllErrors() {
        return this.validationResults.filter(result => result.hasError());
    }
}

class Validator {
    public rules: ValidatorRules = {
        default: {
            validate: () => true,
            modes: ['blur', 'input']
        }
    };

    private readonly i18n: Language;

    constructor(rules: ValidatorRules, i18n: Language) {
        this.setRules(rules);
        this.i18n = i18n;
    }

    setRules(newRules: ValidatorRules) {
        this.rules = {
            ...this.rules,
            ...newRules
        };
    }

    /**
     * Get all validation rules for a field
     */
    private getRulesFor(field: string): ValidatorRule[] {
        let fieldRules: ValidatorRule | ValidatorRule[] = this.rules[field] ?? this.rules['default'];

        if (!Array.isArray(fieldRules)) {
            fieldRules = [fieldRules];
        }

        return fieldRules;
    }

    /**
     * Validates a field
     */
    validate({ key, value, mode = 'blur' }: FieldData, context?: FieldContext) {
        const fieldRules = this.getRulesFor(key);
        // create an ValidationRuleResult, we run the actual validation inside of it
        // validate is called in the constructor of ValidationRuleResult
        // line rule.validate(value, context);
        //
        const validationRulesResult = fieldRules.map(rule => new ValidationRuleResult(rule, value, mode, context, this.i18n));

        return new ValidationResult(validationRulesResult);
    }
}

export default Validator;
